import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import ErrorState from './ErrorState';
import { Chip } from './StatusChip';
import { useConfirm } from './ConfirmDialog';
import { apiFetch, apiErrorMessage } from '../hooks/useApi';
import { formatDate } from '../utils/format';
import { dueStatus } from '../utils/compliance';

/**
 * <SharedDocuments /> — everything a portal link can currently show, in one list.
 *
 * Visibility is a per-file toggle on each property and job, so without this you'd
 * have to open every record to find out what had been shared. This lists what IS
 * shared, says where each file came from, and lets you unshare (with undo).
 */
const setVisible = (f, visible) => apiFetch(`/api/contractor/files/${f.id}`, { method: 'PATCH', body: JSON.stringify({ client_visible: visible }) });

const SharedDocuments = () => {
  const { confirm } = useConfirm();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await apiFetch('/api/contractor/portal-shared-documents');
      setDocs(Array.isArray(d?.documents) ? d.documents : []);
      setError(null);
    } catch (e) { setError(e); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const undo = async (f) => {
    try { await setVisible(f, true); toast.success(`“${f.title || f.file_name}” is shared again`); load(); }
    catch (e) { toast.error(`Couldn't re-share it: ${apiErrorMessage(e)}`); }
  };

  const unshare = async (f) => {
    const name = f.title || f.file_name;
    const ok = await confirm({
      title: `Stop sharing “${name}”?`,
      body: 'Clients with a portal link will no longer see or download it. The file itself is kept.',
      confirmLabel: 'Stop sharing',
    });
    if (!ok) return;
    const before = docs;
    setDocs((prev) => prev.filter((x) => x.id !== f.id));
    try {
      await setVisible(f, false);
      toast.success(({ closeToast }) => (
        <span>
          “{name}” hidden from shared links.{' '}
          <button type="button" className="cw-btn-link" onClick={() => { closeToast?.(); undo(f); }}>Undo</button>
        </span>
      ), { autoClose: 8000 });
    } catch (e) { setDocs(before); toast.error(`Couldn't update that document: ${apiErrorMessage(e)}`); }
  };

  return (
    <section className="cw-card cw-card-pad" aria-labelledby="cw-shared-docs">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        <div>
          <h3 id="cw-shared-docs" className="cw-h3" style={{ margin: 0 }}>
            <i className="fa-solid fa-folder-open" style={{ color: 'var(--cw-blue)', marginRight: 6 }} aria-hidden="true" />
            Documents on shared links
            {docs.length > 0 && <span className="cw-count-pill">{docs.length}</span>}
          </h3>
          <p className="cw-meta" style={{ margin: '2px 0 0' }}>Every file a client can currently open, and which record it came from.</p>
        </div>
        <button type="button" className="cw-btn cw-btn-secondary" onClick={load} disabled={loading}>
          <i className={`fa-solid fa-rotate${loading ? ' fa-spin' : ''}`} aria-hidden="true" /> Refresh
        </button>
      </div>

      {error ? (
        <ErrorState compact thing="shared documents" error={error} onRetry={load} retrying={loading} />
      ) : loading && !docs.length ? (
        <div role="status" className="cw-meta" style={{ padding: 18 }}>Loading…</div>
      ) : docs.length === 0 ? (
        <div role="note" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '12px 14px', background: 'var(--cw-warning-tint)', border: '1px solid var(--cw-warning-border)', borderRadius: 10, fontSize: 'var(--cs-text-sm)', color: 'var(--cw-warning-fg)' }}>
          <i className="fa-solid fa-circle-info" style={{ marginTop: 2 }} aria-hidden="true" />
          <span>
            Nothing is shared yet, so the Documents section on your links will be empty.
            Open a property or a job, go to <strong>Files</strong>, and use the eye icon on a file to make it visible to clients.
          </span>
        </div>
      ) : (
        <div className="cw-share-tablewrap">
          <table className="cw-share-table">
            <caption className="cw-sr-only">Documents visible on shared links</caption>
            <thead>
              <tr><th scope="col">Document</th><th scope="col" style={{ textAlign: 'left' }}>From</th><th scope="col" style={{ textAlign: 'left' }}>Expires</th><th scope="col"><span className="cw-sr-only">Actions</span></th></tr>
            </thead>
            <tbody>
              {docs.map((f) => {
                const exp = f.expire ? dueStatus(f.expire) : null;
                return (
                  <tr key={f.id}>
                    <td>
                      <span style={{ fontWeight: 600 }}>{f.title || f.file_name}</span>
                      {f.document_group && <span className="always" style={{ marginLeft: 7 }}>{f.document_group}</span>}
                    </td>
                    <td style={{ color: 'var(--cw-text-muted)' }}>
                      {f.property_name || 'No property'}
                      {f.job_title && <span> · {f.job_title}</span>}
                    </td>
                    <td>
                      {!exp ? <span style={{ color: 'var(--cw-text-muted)' }}>No expiry</span>
                        : exp.level === 'overdue' ? <Chip tone="danger" label={`Expired ${formatDate(f.expire)}`} dot />
                          : <span style={{ color: 'var(--cw-text-muted)' }}>{formatDate(f.expire)}</span>}
                    </td>
                    <td className="tick" style={{ whiteSpace: 'nowrap' }}>
                      <button type="button" className="cw-btn-link danger" onClick={() => unshare(f)} aria-label={`Stop sharing ${f.title || f.file_name}`}>Unshare</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default SharedDocuments;
